import React, { useState, useEffect } from 'react';
import '../styles/Login.css';
import config from '../config';
import backgroundImage from '../assets/ai-background.jpg';

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ssoConfig, setSsoConfig] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(true);

  // eslint-disable react-hooks/exhaustive-deps
  useEffect(() => {
    const handled = checkSsoCallback();
    if (!handled) {
      fetchSsoConfig();
    }
  }, []);
  // eslint-enable react-hooks/exhaustive-deps

  const fetchSsoConfig = async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/sso/config`, {
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setSsoConfig(data);
      } else {
        setSsoConfig({ enabled: false });
      }
    } catch (error) {
      console.error('Failed to fetch SSO config:', error);
      setSsoConfig({ enabled: false });
    } finally {
      setSsoLoading(false);
    }
  };

  const checkSsoCallback = () => {
    const params = new URLSearchParams(window.location.search);

    const token = params.get('sso_token') || params.get('token');
    const error = params.get('error');
    const username = params.get('username');
    const name = params.get('name');
    const isAdmin = params.get('is_admin');

    console.log('Checking SSO callback:', {
      hasToken: !!token,
      hasError: !!error,
      username,
      name,
      isAdmin
    });

    if (error) {
      setError(decodeURIComponent(error));
      window.history.replaceState({}, document.title, window.location.pathname);
      setSsoLoading(false);
      return true;
    }

    if (token) {
      console.log('SSO token found, logging in...');
      const userFromUrl = {
        username: username || '',
        name: name || '',
        email: username || '',
        is_admin: isAdmin || 'No'
      };
      loginWithSsoToken(token, userFromUrl);
      return true;
    }

    return false;
  };

  /**
   * SSO login: After receiving the SSO token, fetch user-info from backend
   * to get query_engine, query_cap, and other server-side fields.
   * This ensures SSO users get the same engine config as traditional login users.
   */
  const loginWithSsoToken = async (token, userFromUrl) => {
    try {
      setLoading(true);
      console.log('Processing SSO login with user:', userFromUrl);

      // Start with basic user data from URL params
      const userData = {
        username: userFromUrl?.username || '',
        email: userFromUrl?.email || userFromUrl?.username || '',
        name: userFromUrl?.name || '',
        is_admin: userFromUrl?.is_admin || 'No',
        query_cap: 100
      };

      // FIX: Fetch user-info to get query_engine and accurate query_cap
      let query_engine = 'legacy';
      try {
        const infoResponse = await fetch(`${config.apiBaseUrl}/api/user-info`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (infoResponse.ok) {
          const info = await infoResponse.json();
          // Merge server data into userData (server is authoritative)
          userData.query_cap = info.query_cap || userData.query_cap;
          userData.is_admin = info.is_admin || userData.is_admin;
          userData.allow_querying = info.allow_querying || 'No';
          userData.query_engine = info.query_engine || 'legacy';
          query_engine = info.query_engine || 'legacy';
        }
      } catch (infoErr) {
        console.warn('[SSO] Could not fetch user-info, using defaults:', infoErr);
        // Non-fatal: App.js fetchUserInfo will retry on mount
      }

      console.log('SSO login successful, storing user data:', userData);

      localStorage.setItem(config.storage.tokenKey, token);
      localStorage.setItem(config.storage.userKey, JSON.stringify(userData));

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Trigger parent component update WITH query_engine at top level
      console.log('Calling onLogin callback...');
      onLogin({
        success: true,
        token,
        query_engine,
        user: userData
      });

    } catch (err) {
      console.error('SSO login error:', err);
      setError('Authentication failed. Please try again.');
      localStorage.removeItem(config.storage.tokenKey);
      localStorage.removeItem(config.storage.userKey);
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setLoading(false);
      setSsoLoading(false);
    }
  };

  const handleTraditionalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.apiBaseUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (data.success) {
        // FIX: Merge query_engine into user object before storing
        // Backend sends query_engine at top level, not inside data.user
        const userToStore = {
          ...(data.user || { username: credentials.username }),
          query_engine: data.query_engine || 'legacy',
        };
        localStorage.setItem(config.storage.tokenKey, data.token);
        localStorage.setItem(config.storage.userKey, JSON.stringify(userToStore));
        onLogin(data);
      } else {
        setError(data.message || 'Invalid username or password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSsoLogin = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (ssoConfig && ssoConfig.enabled === true) {
      window.location.href = `${config.apiBaseUrl}/api/sso/login`;
    } else {
      setError('SSO is not configured. Please use traditional login.');
    }
  };

  const shouldShowSso = ssoConfig && ssoConfig.enabled === true;

  if (loading && !credentials.username) {
    return (
      <div className="login-page">
        <div className="login-background" style={{ backgroundImage: `url(${backgroundImage})` }} />
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="app-icon">
                <div className="icon-bot">🤖</div>
              </div>
              <h1 className="app-title">Sales AI Agent</h1>
              <p className="app-subtitle">for VBL</p>
              <p className="app-description">Completing sign in...</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #9333ea',
                borderRadius: '50%'
              }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-background" style={{ backgroundImage: `url(${backgroundImage})` }} />
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="app-icon">
              <div className="icon-bot">🤖</div>
            </div>
            <h1 className="app-title">Sales AI Agent</h1>
            <p className="app-subtitle">for VBL</p>
            <p className="app-description">Intelligent Sales Analytics Platform</p>
          </div>

          <form onSubmit={handleTraditionalLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <div className="input-wrapper">
                <input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  placeholder="Enter your username"
                  required
                  disabled={loading}
                  className="form-input"
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  className="form-input"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <button type="submit" className="login-button primary" disabled={loading}>
              {loading ? (
                <div className="button-loader">
                  <div className="spinner"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {!ssoLoading && shouldShowSso && (
            <>
              <div className="divider">
                <span className="divider-text">or</span>
              </div>

              <button
                type="button"
                onClick={handleSsoLogin}
                className="login-button sso-button"
                disabled={loading}
              >
                <svg className="microsoft-icon" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
                  <rect x="12" y="1" width="10" height="10" fill="#00a4ef"/>
                  <rect x="1" y="12" width="10" height="10" fill="#7fba00"/>
                  <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
                </svg>
                <span>Sign in with Microsoft</span>
              </button>
            </>
          )}

          <div className="login-footer">
            <div className="security-badge">
              <span className="security-icon">🔐</span>
              <span>Secure access • Contact administrator for support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
